// Live capture coach — decides whether the framing is good enough to record,
// from a single detected pose plus an optional frame brightness. Pure and
// fully unit-testable; the webcam UI feeds it live frames and renders the
// verdict. Catching a bad clip here (body cut off, too far, too dark) is far
// cheaper than discovering it after analysis.

import { BP, type PoseFrame } from "../pose/landmarks";
import type { GaitView } from "../store/types";

export interface CoachCheck {
  key: "person" | "fullbody" | "framing" | "lighting" | "orientation";
  ok: boolean;
  /** Short label for the checklist chip. */
  label: string;
  /** Actionable hint shown when this is the top issue. */
  hint: string;
  /** Blocking checks must pass for `ready`; advisory ones only nudge. */
  blocking: boolean;
}

export interface CoachVerdict {
  ready: boolean;
  checks: CoachCheck[];
  /** The single most important message right now. */
  hint: string;
}

export interface CoachInput {
  /** Latest detected pose, or null if no person was found. */
  frame: PoseFrame | null;
  view: GaitView;
  /** Mean frame luma 0..1, if available. Omitted ⇒ lighting not assessed. */
  brightness?: number | null;
}

export const COACH_DEFAULTS = {
  minVisibility: 0.5,
  marginX: 0.04, // horizontal safe margin (fraction of frame width)
  edgeY: 0.02, // top/bottom cutoff guard
  minVerticalFill: 0.45, // head→foot must span ≥ this fraction of the frame
  minBrightness: 0.18,
  maxBrightness: 0.97,
};

const KEY_JOINTS = [
  BP.LEFT_SHOULDER,
  BP.RIGHT_SHOULDER,
  BP.LEFT_HIP,
  BP.RIGHT_HIP,
  BP.LEFT_KNEE,
  BP.RIGHT_KNEE,
  BP.LEFT_ANKLE,
  BP.RIGHT_ANKLE,
];

function mean(xs: number[]): number {
  return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0;
}

export function evaluateCapture(
  input: CoachInput,
  cfg = COACH_DEFAULTS,
): CoachVerdict {
  const { frame, view } = input;
  const checks: CoachCheck[] = [];

  // 1 — person present
  const keyVis = frame ? KEY_JOINTS.map((i) => frame.landmarks[i]?.visibility ?? 0) : [];
  const personOk = frame !== null && mean(keyVis) >= 0.4;
  checks.push({
    key: "person",
    ok: personOk,
    label: "Person im Bild",
    hint: "Stell die Person ins Bild.",
    blocking: true,
  });

  // 2 — full body visible
  const fullBodyOk = personOk && keyVis.every((v) => v >= cfg.minVisibility);
  checks.push({
    key: "fullbody",
    ok: fullBodyOk,
    label: "Ganzer Körper sichtbar",
    hint: "Ganzer Körper muss sichtbar sein — Abstand vergrößern.",
    blocking: true,
  });

  // 3 — framing: inside the frame (not cut off) and large enough
  let framingOk = false;
  let framingHint = "Person zu klein — näher heran oder zoomen.";
  if (personOk && frame) {
    const pts = [...KEY_JOINTS, BP.NOSE].map((i) => frame.landmarks[i]).filter(Boolean);
    const xs = pts.map((p) => p.x);
    const ys = pts.map((p) => p.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const insideX = minX >= cfg.marginX && maxX <= 1 - cfg.marginX;
    const insideY = minY >= cfg.edgeY && maxY <= 1 - cfg.edgeY;
    const tallEnough = maxY - minY >= cfg.minVerticalFill;
    framingOk = insideX && insideY && tallEnough;
    if (!insideX || !insideY) framingHint = "Person ist angeschnitten — Kamera weiter weg oder kippen.";
    else if (!tallEnough) framingHint = "Person zu klein — näher heran oder zoomen.";
  }
  checks.push({
    key: "framing",
    ok: framingOk,
    label: "Gut im Bild",
    hint: framingHint,
    blocking: true,
  });

  // 4 — lighting (advisory; only if a brightness reading is supplied)
  const b = input.brightness;
  const lightingOk =
    b === undefined || b === null ? true : b >= cfg.minBrightness && b <= cfg.maxBrightness;
  checks.push({
    key: "lighting",
    ok: lightingOk,
    label: "Licht ok",
    hint:
      b !== undefined && b !== null && b > cfg.maxBrightness
        ? "Zu hell / Gegenlicht — Kamera umstellen."
        : "Mehr Licht — gleichmäßig ausleuchten.",
    blocking: false,
  });

  // 5 — orientation (advisory): side-on for lateral, facing for frontal.
  // Heuristic from shoulder spread vs torso length. Coarse on purpose — only
  // flags an obvious mismatch, never blocks.
  let orientationOk = true;
  if (fullBodyOk && frame) {
    const lS = frame.landmarks[BP.LEFT_SHOULDER];
    const rS = frame.landmarks[BP.RIGHT_SHOULDER];
    const lH = frame.landmarks[BP.LEFT_HIP];
    const rH = frame.landmarks[BP.RIGHT_HIP];
    const shoulderW = Math.abs(lS.x - rS.x);
    const torso = Math.hypot(
      (lS.x + rS.x) / 2 - (lH.x + rH.x) / 2,
      (lS.y + rS.y) / 2 - (lH.y + rH.y) / 2,
    );
    const ratio = torso > 1e-4 ? shoulderW / torso : 0;
    if (view === "lateral") orientationOk = ratio < 1.1;
    else orientationOk = ratio > 0.45;
  }
  checks.push({
    key: "orientation",
    ok: orientationOk,
    label: view === "lateral" ? "Seitlich" : "Frontal",
    hint: view === "lateral" ? "Person seitlich zur Kamera stellen." : "Person frontal zur Kamera stellen.",
    blocking: false,
  });

  const ready = checks.filter((c) => c.blocking).every((c) => c.ok);
  const firstBlocking = checks.find((c) => c.blocking && !c.ok);
  const firstAdvisory = checks.find((c) => !c.blocking && !c.ok);
  const hint = firstBlocking
    ? firstBlocking.hint
    : firstAdvisory
      ? firstAdvisory.hint
      : "Bereit — Aufnahme starten.";

  return { ready, checks, hint };
}
